import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, BrowserRouter } from 'react-router'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import theme from '@/shared/theme/theme'
import { useAuthStore, ProtectedRoute } from '@/features/auth'
import Layout from '@/shared/components/Layout/Layout'
import NotFoundPage from '@/shared/components/NotFoundPage'
import { ErrorBoundary, Toast } from '@/shared/components/ui'
import { useToastStore } from '@/shared/store/toastStore'
import { extractApiError } from '@/shared/lib/api'

const HomeContent        = lazy(() => import('@/features/home/components/HomeContent'))
const SettingsPage       = lazy(() => import('@/features/settings/components/SettingsPage'))
const TrolleyListPage    = lazy(() => import('@/features/trolley/components/TrolleyListPage'))
const TrolleyWizard      = lazy(() => import('@/features/trolley/components/TrolleyWizard'))
const TrolleyDetailPage  = lazy(() => import('@/features/trolley/components/TrolleyDetailPage'))
const TrolleyTemplateListPage = lazy(() => import('@/features/trolley/components/TrolleyTemplateListPage'))
const FoamsListPage      = lazy(() => import('@/features/foams/components/FoamsListPage'))
const FoamEditor         = lazy(() => import('@/features/foams/components/FoamEditor'))

const PageLoadingFallback = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
    </Box>
)

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            retry: 1,
        },
    },
    queryCache: new QueryCache({
        onError: (error) => {
            useToastStore.getState().show(extractApiError(error), 'error')
        },
    }),
    mutationCache: new MutationCache({
        onError: (error) => {
            useToastStore.getState().show(extractApiError(error), 'error')
        },
    }),
})

const App = () => {
    const { initializeAuth } = useAuthStore()

    useEffect(() => {
        void initializeAuth()
    }, [initializeAuth])

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <ProtectedRoute>
                        <ErrorBoundary>
                            <Suspense fallback={<PageLoadingFallback />}>
                                <Routes>
                                    <Route path="/" element={<Layout />}>
                                        <Route index element={<HomeContent />} />
                                        <Route path="settings" element={<SettingsPage />} />
                                        <Route path="foams" element={<FoamsListPage />} />
                                        <Route path="foams/new" element={<FoamEditor />} />
                                        <Route path="foams/:foamId" element={<FoamEditor />} />
                                        <Route path="trolleys" element={<TrolleyListPage />} />
                                        <Route path="trolley-templates" element={<TrolleyTemplateListPage />} />
                                        <Route path="trolleys/new" element={<TrolleyWizard />} />
                                        <Route path="trolleys/:trolleyId" element={<TrolleyDetailPage />} />
                                        <Route path="trolleys/:trolleyId/edit" element={<TrolleyWizard />} />
                                    </Route>
                                    <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                            </Suspense>
                        </ErrorBoundary>
                    </ProtectedRoute>
                    <Toast />
                </ThemeProvider>
            </BrowserRouter>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    )
}

export default App
